/**
 * Styles shared by the sign-in gate's screens (sign-in, agency email, verification code,
 * registration), ported from the le-portal-ui-v3 prototype (src/App.css) onto Fluent tokens.
 *
 * The cards are Fluent `Card`s: `mergeClasses` applies the className passed to a Fluent
 * component after the component's own styles, so these override its padding, gap and
 * layout without `!important`.
 */
import { makeStyles, shorthands, tokens } from '@fluentui/react-components';

/** The prototype's small-screen breakpoint for the gate. */
export const LOGIN_NARROW_MEDIA = '@media (max-width: 620px)';

/** Where footer action rows stack their buttons. */
export const LOGIN_FOOTER_NARROW_MEDIA = '@media (max-width: 600px)';

export const useLoginStyles = makeStyles({
  // The card's vertical rhythm is expressed in vh-relative clamps so it compresses on
  // short screens instead of pushing the page into a scrollbar.
  card: {
    display: 'grid',
    ...shorthands.gap(`clamp(${tokens.spacingVerticalM}, 1.8vh, ${tokens.spacingVerticalXXL})`),
    width: 'min(760px, 100%)',
    ...shorthands.padding(
      `clamp(${tokens.spacingVerticalL}, 2.2vh, 40px)`,
      `clamp(${tokens.spacingHorizontalXL}, 4vw, 40px)`,
    ),
    backgroundColor: tokens.colorNeutralBackground1,
    [LOGIN_NARROW_MEDIA]: {
      ...shorthands.gap(tokens.spacingVerticalXL),
      ...shorthands.padding(tokens.spacingVerticalXL),
    },
    '@media (forced-colors: active)': {
      color: 'CanvasText',
      backgroundColor: 'Canvas',
      ...shorthands.borderColor('ButtonText'),
      boxShadow: 'none',
    },
  },

  cardNarrow: {
    width: 'min(600px, 100%)',
  },

  headingIcon: {
    display: 'grid',
    placeItems: 'center',
    width: '48px',
    height: '48px',
    color: tokens.colorBrandForeground1,
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
    '& svg': {
      fontSize: '24px',
    },
    [LOGIN_NARROW_MEDIA]: {
      width: '40px',
      height: '40px',
    },
  },

  stepHeading: {
    display: 'grid',
    ...shorthands.gap(tokens.spacingVerticalL),
  },

  centeredHeading: {
    display: 'grid',
    justifyItems: 'center',
    textAlign: 'center',
    '& h1': {
      ...shorthands.margin(0, 0, tokens.spacingVerticalL),
    },
    '& p': {
      ...shorthands.margin(0),
      color: tokens.colorNeutralForeground3,
      lineHeight: 1.5,
    },
  },

  centeredHeadingIcon: {
    marginBottom: tokens.spacingVerticalM,
  },

  // Each step wraps its fields and its primary button in a form so Enter submits. The form
  // then stands where several children of the card's grid used to, so it repeats the card's
  // gap. `inherit` keeps it following the card, including the narrower small-screen gap.
  stepForm: {
    display: 'grid',
    gap: 'inherit',
  },

  formFields: {
    display: 'grid',
    ...shorthands.gap(tokens.spacingVerticalXXL),
  },

  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    ...shorthands.gap(tokens.spacingHorizontalM),
    [LOGIN_NARROW_MEDIA]: {
      alignItems: 'stretch',
      flexDirection: 'column-reverse',
      '& button': {
        width: '100%',
      },
    },
  },

  footerActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.gap(tokens.spacingHorizontalM),
    paddingTop: tokens.spacingVerticalL,
    ...shorthands.borderTop(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    '& > :only-child': {
      marginLeft: 'auto',
    },
    [LOGIN_FOOTER_NARROW_MEDIA]: {
      flexDirection: 'column-reverse',
      alignItems: 'stretch',
      '& button': {
        width: '100%',
      },
    },
  },
});
